import callOutcomes from './callOutcomes';
import roles from './roles';

/**
 * The states the scheduler watches: the conversations the agent is still
 * holding. Mirrors LIVE_STATUS in the cloud functions.
 */
export const LIVE_STATUS = ['starting', 'waiting', 'active'];

/**
 * The states in which a conversation can still send a message, and so the
 * only ones worth stopping. It mirrors SPEAKING_STATUS in the cloud
 * functions, `abandoned` included: after a handoff the agent keeps replying,
 * which is exactly when an operator wants the lead back.
 */
export const SPEAKING_STATUS = [...LIVE_STATUS, 'abandoned'];

/** The same rule stopMatchingConversations applies before touching anything. */
export const canBeStopped = (conversation) =>
  !!conversation &&
  !conversation.stoppedAt &&
  SPEAKING_STATUS.includes(conversation.status);

/**
 * What each state is called for whoever reads it. The stored values are the
 * vocabulary of the engine — `abandoned` above all, which does not mean the
 * lead was dropped but that it went back to the call center.
 *
 * The colour stays quiet on purpose: the outcome is what an operator looks
 * for, and it carries the CRM's own colours from config/callOutcomes.
 */
export const CONVERSATION_STATUS = {
  starting: { title: 'Opening', color: 'light' },
  waiting: { title: 'Waiting for a reply', color: 'light' },
  active: { title: 'In progress', color: 'info' },
  // Not 'abandoned': the agent handed the conversation to a person, and
  // the call is back in the operator queue. Said as the action the agent
  // took, because under a column called AI Status a place reads like
  // where the lead is rather than what the agent did.
  abandoned: { title: 'Handed to an operator', color: 'light' },
  completed: { title: 'Finished', color: 'light' },
  failed: { title: 'Failed', color: 'danger' },
};

/** Why the agent let go. Same reason: these are engine words. */
export const HANDOFF_REASONS = {
  noReply: 'Never replied',
  stalled: 'Stopped replying',
  unreachable: 'Number unreachable',
  error: 'Technical error',
  closureNeedsHuman: 'Closure needs a person',
  prequalified: 'Prequalified',
  maxTurns: 'Too many turns',
  stopped: 'Stopped by hand',
  manual: 'Taken back by the call center',
};

/**
 * Who may do what with the agent's conversations. Mirrors
 * functions/aiConversation/stopPermission.js, by hand: the callable is what
 * actually decides, these only keep a button from being shown to someone the
 * callable would refuse. A selection is the floor deciding that an operator
 * calls now; everything at once is the emergency brake.
 */
export const TAKE_BACK_ROLES = [
  roles.SUPER_ADMIN,
  roles.ADMIN,
  roles.MANAGER,
  roles.TEAM_LEADER,
];
export const STOP_ALL_ROLES = [roles.SUPER_ADMIN, roles.ADMIN];

const NOTHING_TO_SAY = { title: '', color: 'light' };

/**
 * What became of the conversation, in one phrase, for a table with one cell
 * per lead. `title` is an English key: translate it where it is shown.
 *
 * The order of the branches IS the function:
 *
 * - `completed` first, because the emergency brake writes `stoppedAt` even
 *   over a conversation already closed by an outcome, so a branch on that
 *   field would cover a real closure with "stopped by hand";
 * - `stoppedAt` needs no branch of its own, because a stop hands the
 *   conversation over before writing the field, and `handoffReason` therefore
 *   already carries `stopped` or `manual`.
 */
export const conversationClosure = (conversation) => {
  if (!conversation) return NOTHING_TO_SAY;

  const { status, outcome, handoffReason } = conversation;

  if (status === 'completed') {
    const closure = callOutcomes[outcome];
    return closure
      ? { title: closure.title, color: closure.color, icon: closure.icon }
      : { title: 'Finished without an outcome', color: 'secondary' };
  }

  if (status === 'failed') return { title: 'Failed', color: 'danger' };

  if (status === 'abandoned')
    return {
      title: HANDOFF_REASONS[handoffReason] || CONVERSATION_STATUS.abandoned.title,
      color:
        handoffReason === 'prequalified'
          ? 'info'
          : handoffReason === 'stopped' || handoffReason === 'manual'
          ? 'dark'
          : 'warning',
    };

  if (LIVE_STATUS.includes(status))
    return { title: 'Still with the agent', color: 'info' };

  return NOTHING_TO_SAY;
};
